import React, { useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 4000,
  onClose
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500',
          icon: CheckCircleIcon
        };
      case 'error':
        return {
          bg: 'bg-red-500',
          icon: XCircleIcon
        };
      case 'info':
        return {
          bg: 'bg-blue-500',
          icon: InformationCircleIcon
        };
      case 'warning':
        return {
          bg: 'bg-amber-500',
          icon: ExclamationTriangleIcon
        };
    }
  };

  const { bg, icon: Icon } = getToastStyles();

  return (
    <div
      className={`${bg} text-white rounded-lg shadow-2xl p-4 mb-3 w-80 max-w-[90vw]
                  animate-slide-in-right flex items-start gap-3`}
      role="alert"
    >
      <Icon className="h-6 w-6 flex-shrink-0 mt-0.5" />

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{title}</p>
        {message && (
          <p className="text-sm opacity-90 mt-1">{message}</p>
        )}
      </div>

      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
        aria-label="Close notification"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

export default Toast;
